import {Source, Layer} from '../MaptilerWrapper'
import {useMemo} from 'react'
import PropTypes from 'prop-types'
import {useTheme} from '@mui/material/styles'
import {useConfig} from '../../../../../store/hooks/config'
import {formatPercentage, generateColorStops, getMapColors, getPaintProperty} from '../helpers'
import {selectSelectedGradientView, selectSelectedPopulationViewMode} from '../../../../../store/slices/focusLayerSlice'
import {useAppSelector} from '../../../../../store/hooks'

const CongressionalDistrictLayer = ({
  congressionalDistrictData,
  congressionalDistrictTilesetSourceLayer,
  visibility,
  congressionalDistrictFipsToPersona,
  personasToggle
}) => {
  const theme = useTheme()
  const mapColors = useMemo(() => getMapColors(theme), [theme])
  const {personasConfig} = useConfig()
  const selectedGradientView = useAppSelector(selectSelectedGradientView)
  const selectedPopulationViewMode = useAppSelector(selectSelectedPopulationViewMode)

  // Deduplicated CD records
  const filteredData = useMemo(() => {
    if (!congressionalDistrictData?.congressionalDistrict?.congressionalDistrict) return null
    const lastOccurrenceMap = new Map()
    for (let i = congressionalDistrictData.congressionalDistrict.congressionalDistrict.length - 1; i >= 0; i--) {
      const cd = congressionalDistrictData.congressionalDistrict.congressionalDistrict[i]
      if (!lastOccurrenceMap.has(cd.congressional_district_id)) {
        lastOccurrenceMap.set(cd.congressional_district_id, cd)
      }
    }
    return Array.from(lastOccurrenceMap.values())
  }, [congressionalDistrictData])

  // Counts aggregation
  const populationData = useMemo(() => {
    if (!filteredData) return null
    return [
      ...filteredData.map((cd) => [cd.congressional_district_id, cd.count]),
      ...(congressionalDistrictData?.congressionalDistrict?.missingGeoIds ?? []).map((fips) => [fips, 0])
    ]
  }, [filteredData, congressionalDistrictData])

  // Mode 1 — selected CD / total state population (parent geography)
  const aggForStateTotal = useMemo(() => {
    if (!filteredData) return null
    return [
      ...filteredData.map((cd) => [cd.congressional_district_id, formatPercentage(cd.statePercentage)]),
      ...(congressionalDistrictData?.congressionalDistrict?.missingGeoIds ?? []).map((fips) => [fips, 0])
    ]
  }, [filteredData, congressionalDistrictData])

  // Mode 2 — selected CD / total CD population (same geography)
  const aggForCDTotal = useMemo(() => {
    if (!filteredData) return null
    return [
      ...filteredData.map((cd) => [cd.congressional_district_id, formatPercentage(cd.congressionalDistrictPercentage)]),
      ...(congressionalDistrictData?.congressionalDistrict?.missingGeoIds ?? []).map((fips) => [fips, 0])
    ]
  }, [filteredData, congressionalDistrictData])

  const colors = useMemo(() => {
    if (!populationData) return null
    return generateColorStops(populationData, mapColors, false)
  }, [populationData, mapColors])

  const colorsForStateTotal = useMemo(() => {
    if (!aggForStateTotal?.length) return null
    return generateColorStops(aggForStateTotal, mapColors, true)
  }, [aggForStateTotal, mapColors])

  const colorsForCDTotal = useMemo(() => {
    if (!aggForCDTotal?.length) return null
    return generateColorStops(aggForCDTotal, mapColors, true)
  }, [aggForCDTotal, mapColors])

  const layerType = personasToggle ? 'personas' : 'congressionalDistrict'

  const colorsMap = (() => {
    if (selectedGradientView === 'population') return colors
    if (selectedPopulationViewMode === 'selected_of_state_total') return colorsForStateTotal
    return colorsForCDTotal
  })()

  const aggregationMap = (() => {
    if (selectedGradientView === 'population') return populationData
    if (selectedPopulationViewMode === 'selected_of_state_total') return aggForStateTotal
    return aggForCDTotal
  })()

  return (
    populationData &&
    colors && (
      <>
        <Source
          id='congressionaldistrict'
          type='vector'
          url={'https://api.maptiler.com/tiles/01966741-d34f-726a-a62c-cb8d938c0421/tiles.json'}
          promoteId='GEOID'
        >
          <Layer
            beforeId='state-border'
            id='congressionaldistrict-border'
            type='line'
            source='congressionaldistrict'
            source-layer={congressionalDistrictTilesetSourceLayer}
            paint={{
              'line-color': theme.palette.hpWhite, // White outline color
              'line-width': 0.8,
              'line-opacity': 1 // Set the outline thickness (adjust as needed)
            }}
          />
          {aggregationMap && colorsMap && (
            <Layer
              beforeId='congressionaldistrict-border'
              id='congressionaldistrict-fill'
              type='fill'
              source='congressionaldistrict'
              source-layer={congressionalDistrictTilesetSourceLayer}
              filter={['in', ['get', 'GEOID'], ['literal', aggregationMap.map((d) => d[0])]]}
              paint={getPaintProperty(
                layerType,
                congressionalDistrictFipsToPersona,
                personasConfig,
                aggregationMap,
                colorsMap
              )}
              layout={{visibility}}
              data-testid='congressionaldistrict-fill'
            />
          )}

          <Layer
            id='congressionaldistrict-border-onhover'
            type='line'
            source='congressionaldistrict'
            source-layer={congressionalDistrictTilesetSourceLayer}
            paint={{
              'line-color': theme.palette.hpAccentBlack,
              'line-width': 2,
              'line-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 1, 0]
            }}
          />
          <Layer
            id='congressionaldistrict-border-onhover-stroke'
            type='line'
            source='congressionaldistrict'
            source-layer={congressionalDistrictTilesetSourceLayer}
            paint={{
              'line-color': theme.palette.hpWhite,
              'line-width': 2,
              'line-opacity': ['case', ['boolean', ['feature-state', 'hover'], false], 1, 0],
              'line-gap-width': 2
            }}
          />
        </Source>

        <Source
          id='congressionaldistrict-label'
          type='vector'
          url={'https://api.maptiler.com/tiles/01971d69-223e-75f8-ad1e-9c93c05b98a6/tiles.json'}
          promoteId='GEOID'
        >
          {/* Below layer will display congressional district labels thorugh the tiles via maptiler cloud when chloropath is on */}
          <Layer
            id='congressionaldistrict-symbol'
            type='symbol'
            source='congressionaldistrict-label'
            minzoom={3}
            maxzoom={15}
            source-layer={congressionalDistrictTilesetSourceLayer}
            filter={['in', ['get', 'GEOID'], ['literal', populationData ? populationData.map((d) => d[0]) : []]]}
            layout={{
              'text-field': ['format', ['get', 'NAMELSAD']],
              'text-font': ['Noto Sans Extra Bold'],
              'text-padding': 2,
              'text-size': {
                base: 3,
                stops: [
                  [3, 10],
                  [6, 18]
                ]
              },
              'text-max-width': 8,
              'text-offset': [0, 0],
              'text-transform': 'uppercase',
              'text-anchor': 'center'
            }}
            paint={{
              'text-color': theme.palette.hpAccentBlack,
              'text-halo-color': theme.palette.hpWhite,
              'text-halo-width': 1.5
            }}
          />
        </Source>
      </>
    )
  )
}

CongressionalDistrictLayer.propTypes = {
  congressionalDistrictData: PropTypes.object,
  congressionalDistrictTilesetSourceLayer: PropTypes.string,
  visibility: PropTypes.string,
  congressionalDistrictFipsToPersona: PropTypes.object,
  personasToggle: PropTypes.bool
}

export default CongressionalDistrictLayer

/**
 * Summary of the file:
 * This file defines the `CongressionalDistrictLayer` React component, which renders a Mapbox layer for visualizing congressional district data.
 * It supports two visualization modes: personas and population density. The component dynamically calculates color scales and filters based on the data provided.
 * It uses Mapbox GL JS layers (`fill`, `line`, and `symbol`) to display district boundaries, labels, and hover effects.
 *
 * Functions:
 * 1. `getPersonaPaintProperty`:
 *    - Generates the paint property for the "personas" visualization mode.
 *    - Maps FIPS codes to persona colors using the `getPersonaColor` helper.
 *
 * 2. `getDefaultPaintProperty`:
 *    - Generates the paint property for the "congressionalDistrict" visualization mode.
 *    - Uses population data to create an interpolated color scale.
 *
 * 3. `getPaintProperty`:
 *    - Determines which paint property function to use based on the `layerType`.
 *
 * 4. `CongressionalDistrictLayer`:
 *    - Main React component that renders the Mapbox layers.
 *    - Dynamically calculates population data and color scales using `useEffect`.
 *    - Supports toggling between personas and population density visualizations.
 *    - Renders multiple Mapbox layers for fill, border, labels, and hover effects.
 *
 * Props:
 * - `congressionalDistrictData`: Object containing congressional district data.
 * - `congressionalDistrictTilesetSourceLayer`: String specifying the source layer for the tileset.
 * - `visibility`: String controlling the visibility of the layer.
 * - `congressionalDistrictFipsToPersona`: Object mapping FIPS codes to personas.
 * - `personasToggle`: Boolean to toggle between personas and population density modes.
 */
